import React, { useState, useEffect } from 'react';
import { List, Avatar, Typography, Badge, Spin, message, Input, Button, Modal, Descriptions, Tag, Space } from 'antd';
import {
    UserOutlined,
    SearchOutlined,
    MoreOutlined,
    ArrowLeftOutlined,
    MailOutlined,
    IdcardOutlined,
    MessageFilled,
    EllipsisOutlined,
    SettingOutlined,
    PhoneOutlined,
} from '@ant-design/icons';
import ChatServ from '../service/ChatServ';
import UserServ from '../service/UserServ';
import ChatWindow from './ChatWindow';
import { getImageUrl } from './SafeImage';
import dayjs from 'dayjs';
import '../css/ChatApp.css';
import EmptyState from '../util/EmptyState';

const { Text, Title } = Typography;

const ChatList = () => {
    const [conversations, setConversations] = useState([]);
    const [filteredConversations, setFilteredConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedConv, setSelectedConv] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userModalVisible, setUserModalVisible] = useState(false);
    const [userLoading, setUserLoading] = useState(false);
    const [searchText, setSearchText] = useState('');

    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const currentUserId = currentUser?._id || currentUser?.id;

    useEffect(() => {
        fetchConversations();
    }, []);

    useEffect(() => {
        if (!searchText.trim()) {
            setFilteredConversations(conversations);
        } else {
            const filtered = conversations.filter(conv => {
                const other = getOtherParticipant(conv);
                const name = `${other?.firstName} ${other?.lastName}`.toLowerCase();
                return name.includes(searchText.toLowerCase());
            });
            setFilteredConversations(filtered);
        }
    }, [searchText, conversations]);

    const fetchConversations = async () => {
        setLoading(true);
        try {
            const res = await ChatServ.getConversations();
            setConversations(res.data || []);
            setFilteredConversations(res.data || []);
        } catch (error) {
            console.error("Error fetching conversations:", error);
            message.error("Failed to load conversations");
        } finally {
            setLoading(false);
        }
    };

    const handleConvClick = (conv) => {
        setSelectedConv(conv);
    };

    const formatTime = (date) => {
        const d = dayjs(date);
        const now = dayjs();
        if (d.isSame(now, 'day')) return d.format('h:mm A');
        if (d.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
        if (d.isSame(now, 'year')) return d.format('MMM D');
        return d.format('DD/MM/YYYY');
    };

    const handleUserClick = async (e, userId) => {
        e.stopPropagation();
        if (!["admin", "superadmin"].includes(currentUser.role)) return;

        setUserLoading(true);
        setUserModalVisible(true);
        try {
            const res = await UserServ.single(userId);
            setSelectedUser(res.data);
        } catch (error) {
            console.error("Error fetching user details:", error);
            message.error("Failed to load user details");
            setUserModalVisible(false);
        } finally {
            setUserLoading(false);
        }
    };

    const getOtherParticipant = (conv) => {
        if (!conv || !conv.participants) return null;
        return conv.participants.find(p => (p._id || p) !== currentUserId);
    };

    return (
        <div className="chat-app-container pcu-h-100 pcu-border-none">
            {/* Sidebar */}
            <div className={`chat-sidebar ${selectedConv ? 'hidden' : ''}`}>
                <div className="sidebar-header">
                    <div className="pcu-flex pcu-align-center pcu-gap-12">
                        <Badge dot status="success" offset={[-6, 32]}>
                            <Avatar 
                                size={44} 
                                src={getImageUrl(currentUser?.profilePhoto)} 
                                icon={<UserOutlined />} 
                                style={{ border: '2px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                            />
                        </Badge>
                        <Title level={4} style={{ margin: 0, fontWeight: 800, letterSpacing: '-0.5px' }}>Messages</Title>
                    </div>
                    <Space>
                        <Button type="text" shape="circle" icon={<SettingOutlined className="pcu-font-18 pcu-color-muted" />} />
                        <Button type="text" shape="circle" icon={<EllipsisOutlined className="pcu-font-20 pcu-color-muted" />} />
                    </Space>
                </div>

                <div className="sidebar-search">
                    <Input
                        placeholder="Search or start new chat"
                        prefix={<SearchOutlined className="pcu-color-muted" />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>

                <div className="conversation-list">
                    {loading ? (
                        <div className="pcu-text-center pcu-padding-50"><Spin /></div>
                    ) : filteredConversations.length === 0 ? (
                        <div className="pcu-padding-x-10 pcu-padding-y-40">
                            <EmptyState
                                title="No Conversations"
                                description={searchText ? "No chats match your search." : "No messages yet. Your conversations will appear here."}
                                gradient="blue"
                                showAction={false}
                                icon="inbox"
                                size="small"
                            />
                        </div>
                    ) : (
                        filteredConversations.map((item) => {
                            const other = getOtherParticipant(item);
                            const isActive = selectedConv?._id === item._id;
                            const isUnread = item.lastMessage && !item.lastMessage.isRead && item.lastMessage.receiver === currentUserId;

                            return (
                                <div
                                    key={item._id}
                                    className={`conv-item ${isActive ? 'active' : ''}`}
                                    onClick={() => handleConvClick(item)}
                                >
                                    <Badge dot={isUnread} offset={[-5, 5]}>
                                        <Avatar size={50} src={getImageUrl(other?.profilePhoto)} icon={<UserOutlined />} />
                                    </Badge>
                                    <div className="conv-info">
                                        <div className="conv-top">
                                            <span className="conv-name">{other?.firstName} {other?.lastName}</span>
                                            <span className="conv-time">
                                                {formatTime(item.updatedAt)}
                                            </span>
                                        </div>
                                        <div className="conv-last-msg">
                                            {item.lastMessage?.text || "Started a conversation"}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Main Chat Area */}
            <div className={`chat-main ${selectedConv ? 'active' : ''}`}>
                {selectedConv ? (
                    <div className="pcu-flex-column pcu-h-100">
                        <div className="chat-header">
                            <Button
                                type="text"
                                icon={<ArrowLeftOutlined />}
                                className="mobile-only pcu-margin-right-10"
                                onClick={() => setSelectedConv(null)}
                            />
                            <Avatar
                                size="large"
                                src={getImageUrl(getOtherParticipant(selectedConv)?.profilePhoto)}
                                icon={<UserOutlined />}
                                onClick={(e) => handleUserClick(e, getOtherParticipant(selectedConv)?._id || getOtherParticipant(selectedConv))}
                                className={["admin", "superadmin"].includes(currentUser.role) ? "pcu-cursor-pointer" : ""}
                            />
                            <div
                                className={`pcu-margin-left-15 pcu-flex-1 ${["admin", "superadmin"].includes(currentUser.role) ? "pcu-cursor-pointer" : ""}`}
                                onClick={(e) => handleUserClick(e, getOtherParticipant(selectedConv)?._id || getOtherParticipant(selectedConv))}
                            >
                                <div className="pcu-font-700 pcu-font-16">
                                    {getOtherParticipant(selectedConv)?.firstName} {getOtherParticipant(selectedConv)?.lastName}
                                </div>
                                <div className="pcu-font-12 pcu-color-muted">
                                    {selectedConv.shopId ? `Shop: ${selectedConv.shopId.name}` : 'Support Agent'}
                                </div>
                            </div>
                        </div>

                        <ChatWindow
                            isPanel={true}
                            conversation={selectedConv}
                            currentUser={currentUser}
                            onMessageSent={fetchConversations}
                        />
                    </div>
                ) : (
                    <div className="chat-welcome">
                        <div className="welcome-card">
                            <div className="welcome-icon-wrap">
                                <MessageFilled style={{ fontSize: '48px', color: 'var(--chat-accent)' }} />
                            </div>
                            <h2>Nearzon Web</h2>
                            <p>Send and receive messages with our support team in real-time. Experience secure and fast communication.</p>
                            {/* <Button 
                                type="primary" 
                                size="large" 
                                style={{ 
                                    marginTop: '24px', 
                                    borderRadius: '12px', 
                                    height: '48px', 
                                    padding: '0 32px',
                                    backgroundColor: 'var(--chat-accent)',
                                    fontWeight: 600
                                }}
                            >
                                Start Messaging
                            </Button> */}
                        </div>
                    </div>
                )}
            </div>

            <Modal
                title={null}
                open={userModalVisible}
                onCancel={() => setUserModalVisible(false)}
                footer={null}
                centered
                className="premium-modal"
                width={450}
                bodyStyle={{ padding: 0 }}
            >
                {userLoading ? (
                    <div className="pcu-text-center pcu-padding-50"><Spin size="large" /></div>
                ) : selectedUser ? (
                    <div className="pcu-flex-column">
                        {/* Modal Header with Gradient */}
                        <div style={{ 
                            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', 
                            padding: '40px 20px', 
                            textAlign: 'center',
                            borderRadius: '16px 16px 0 0',
                            borderBottom: '1px solid #e2e8f0'
                        }}>
                            <Avatar 
                                size={100} 
                                src={getImageUrl(selectedUser.profilePhoto)} 
                                icon={<UserOutlined />} 
                                style={{ border: '4px solid #fff', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}
                            />
                            <Title level={3} style={{ margin: '16px 0 4px', fontWeight: 800 }}>
                                {selectedUser.firstName} {selectedUser.lastName}
                            </Title>
                            <Tag 
                                color={selectedUser.role === 'seller' ? 'orange' : 'blue'} 
                                bordered={false}
                                style={{ borderRadius: '6px', fontWeight: 600, padding: '2px 12px' }}
                            >
                                {selectedUser.role?.toUpperCase()}
                            </Tag>
                        </div>

                        {/* Modal Content */}
                        <div style={{ padding: '24px' }}>
                            <div className="pcu-flex-column pcu-gap-20">
                                <div className="pcu-flex pcu-align-center pcu-gap-15" style={{ padding: '12px', background: '#f8fafc', borderRadius: '12px' }}>
                                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <MailOutlined style={{ color: '#4f46e5', fontSize: '18px' }} />
                                    </div>
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Email Address</Text>
                                        <Text strong>{selectedUser.email}</Text>
                                    </div>
                                </div>

                                <div className="pcu-flex pcu-align-center pcu-gap-15" style={{ padding: '12px', background: '#f8fafc', borderRadius: '12px' }}>
                                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <PhoneOutlined style={{ color: '#f97316', fontSize: '18px' }} />
                                    </div>
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Phone Number</Text>
                                        <Text strong>{selectedUser.phone || "Not provided"}</Text>
                                    </div>
                                </div>

                                <div className="pcu-flex pcu-align-center pcu-gap-15" style={{ padding: '12px', background: '#f8fafc', borderRadius: '12px' }}>
                                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <IdcardOutlined style={{ color: '#22c55e', fontSize: '18px' }} />
                                    </div>
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>User ID</Text>
                                        <Text strong style={{ fontSize: '13px' }}>{selectedUser._id}</Text>
                                    </div>
                                </div>
                            </div>

                            <Button 
                                block 
                                size="large" 
                                style={{ marginTop: '32px', borderRadius: '12px', height: '48px', fontWeight: 600 }}
                                onClick={() => setUserModalVisible(false)}
                            >
                                Close Profile
                            </Button>
                        </div>
                    </div>
                ) : null}
            </Modal>
        </div>
    );
};

export default ChatList;
